import { apiGet, apiPost } from "../api-client"

// ============================================================
// TypeScript Interfaces for NM Well Data
// ============================================================

/**
 * Casing record from NM OCD casing table
 */
export interface NMCasingRecord {
  string_type: string        // "Surface Casing", "Production Casing", etc.
  taper: number | null
  date_set: string | null
  diameter_in: number | null
  top_ft: number | null
  bottom_ft: number | null
  grade: string | null
  length_ft: number | null
  weight_ppf: number | null
  cement_bottom_ft: number | null
  cement_top_ft: number | null
  cement_method: string | null
  cement_class: string | null
  cement_sacks: number | null
  pressure_test: boolean | null
}

/**
 * Perforation interval from well completions
 */
export interface NMPerforationInterval {
  top_md_ft: number | null
  bottom_md_ft: number | null
  top_vd_ft: number | null
  bottom_vd_ft: number | null
}

/**
 * Completion record from NM OCD
 */
export interface NMCompletionRecord {
  completion_id: string | null
  completion_name: string | null
  status: string | null
  last_produced: string | null
  bottomhole_location: string | null
  acreage: string | null
  production_method: string | null
  flowing_tubing_pressure_psi: number | null
  choke_size_in: number | null
  gas_volume_mcf: number | null
  gas_oil_ratio: number | null
  oil_volume_bbls: number | null
  water_volume_bbls: number | null
  initial_effective_date: string | null
  most_recent_approval: string | null
  ready_to_produce_date: string | null
  c104_approval_date: string | null
  perforations: NMPerforationInterval[]
}

/**
 * Formation top entry
 */
export interface NMFormationTop {
  formation_name: string
  top_ft: number | null
  producing: boolean | null
  method_obtained: string | null
}

/**
 * Event dates from NM OCD well record
 */
export interface NMEventDates {
  initial_apd_approval: string | null
  most_recent_apd_approval: string | null
  current_apd_expiration: string | null
  spud_date: string | null
  completion_date: string | null
  first_production_date: string | null
  last_inspection: string | null
  last_mit_bht: string | null
  plugging_date: string | null
  ta_date: string | null
}

/**
 * Well data scraped from NM OCD website
 */
export interface NMWellData {
  api10: string            // "30-015-28692"
  api14: string            // "30015286920000"
  well_name: string
  well_number: string | null
  operator_name: string
  operator_number: string
  status: string           // "PRODUCING", "DRILLING", "PLUGGED"
  well_type: string        // "OIL", "GAS"
  work_type: string
  direction: string        // "VERTICAL", "HORIZONTAL"
  multi_lateral: boolean | null
  mineral_owner: string
  surface_owner: string
  surface_location: string
  latitude: number | null
  longitude: number | null
  datum: string
  gl_elevation_ft: number | null
  kb_elevation_ft: number | null
  df_elevation_ft: number | null
  sing_mult_compl: string
  potash_waiver: boolean | null
  formation: string
  proposed_formation: string
  proposed_depth_ft: number | null
  measured_vertical_depth_ft: number | null
  true_vertical_depth_ft: number | null
  plugback_measured_ft: number | null
  // Legacy fields
  elevation_ft: number | null
  tvd_ft: number | null
  spud_date: string | null
  completion_date: string | null
  // Structured data
  event_dates: NMEventDates | null
  casing_records: NMCasingRecord[]
  completions: NMCompletionRecord[]
  formation_tops: NMFormationTop[]
}

/**
 * Document from NM OCD imaging portal
 */
export interface NMDocument {
  filename: string
  url: string
  file_size: string | null
  date: string | null
  doc_type: string | null  // "C-101", "C-103", "C-105"
  doc_type_display?: string | null  // e.g. "NM C-105 - Completion Report", or null (absent on locally-constructed lists)
}

/**
 * Response from document list endpoint
 */
export interface NMDocumentListResponse {
  api: string
  documents: NMDocument[]
  total: number
}

/**
 * Response from combined PDF endpoint
 */
export interface CombinedPDFResponse {
  api: string
  url: string
  document_count: number
}

/**
 * Result from importing a single NM well
 */
export interface ImportResult {
  success: boolean
  well_id: number
  api14: string
  message: string
}

/**
 * Result from batch importing multiple NM wells
 */
export interface BatchImportResult {
  total: number
  successful: number
  failed: number
  results: ImportResult[]
}

// ============================================================
// API Functions
// ============================================================

/**
 * Fetch scraped well data from NM OCD website
 *
 * @param api - The API number (accepts various formats)
 * @returns Scraped well data from OCD
 *
 * @example
 * ```typescript
 * const wellData = await fetchNMWell("30-015-28692")
 * console.log(wellData.well_name, wellData.operator_name)
 * ```
 */
export async function fetchNMWell(api: string): Promise<NMWellData> {
  console.log("[nm-api] Fetching NM well data:", api)

  const response = await apiGet<NMWellData>(`/api/nm/wells/${encodeURIComponent(api)}/`)

  console.log("[nm-api] Well data received:", response)

  return response
}

/**
 * List available documents for an NM well from OCD imaging portal
 *
 * @param api - The API number
 * @returns List of documents with download URLs
 *
 * @example
 * ```typescript
 * const { documents } = await listNMDocuments("30-015-28692")
 * documents.forEach(doc => console.log(doc.filename, doc.url))
 * ```
 */
export async function listNMDocuments(api: string): Promise<NMDocumentListResponse> {
  console.log("[nm-api] Listing NM documents:", api)

  const response = await apiGet<NMDocumentListResponse>(`/api/nm/wells/${encodeURIComponent(api)}/documents/`)

  console.log("[nm-api] Documents received:", response)

  return response
}

/**
 * Get URL for combined PDF of all well documents
 *
 * @param api - The API number
 * @returns URL to download combined PDF
 *
 * @example
 * ```typescript
 * const { url } = await getCombinedPDFUrl("30-015-28692")
 * window.open(url, '_blank')
 * ```
 */
export async function getCombinedPDFUrl(api: string): Promise<CombinedPDFResponse> {
  console.log("[nm-api] Getting combined PDF URL:", api)

  const response = await apiGet<CombinedPDFResponse>(`/api/nm/wells/${encodeURIComponent(api)}/documents/download/`)

  console.log("[nm-api] Combined PDF URL received:", response)

  return response
}

/**
 * Import a single NM well into the system
 *
 * @param api - The API number
 * @param workspaceId - Optional workspace ID to import into
 * @returns Import result with well ID
 *
 * @example
 * ```typescript
 * const result = await importNMWell("30-015-28692", 1)
 * if (result.success) {
 *   console.log(`Well imported with ID: ${result.well_id}`)
 * }
 * ```
 */
export async function importNMWell(api: string, workspaceId?: number): Promise<ImportResult> {
  console.log("[nm-api] Importing NM well:", api, workspaceId)

  const body: { api: string; workspace_id?: number } = { api }
  if (workspaceId !== undefined) {
    body.workspace_id = workspaceId
  }

  const response = await apiPost<ImportResult>("/api/nm/import/", body)

  console.log("[nm-api] Import result:", response)

  return response
}

/**
 * Batch import multiple NM wells into the system
 *
 * @param apis - Array of API numbers
 * @param workspaceId - Optional workspace ID to import into
 * @returns Batch import results
 *
 * @example
 * ```typescript
 * const result = await batchImportNMWells(["30-015-28692", "30-015-28693"], 1)
 * console.log(`Imported ${result.successful}/${result.total} wells`)
 * ```
 */
export async function batchImportNMWells(apis: string[], workspaceId?: number): Promise<BatchImportResult> {
  console.log("[nm-api] Batch importing NM wells:", apis, workspaceId)

  const body: { apis: string[]; workspace_id?: number } = { apis }
  if (workspaceId !== undefined) {
    body.workspace_id = workspaceId
  }

  const response = await apiPost<BatchImportResult>("/api/nm/batch-import/", body)

  console.log("[nm-api] Batch import result:", response)

  return response
}
